<script setup lang="tsx">
import IconButton from '@/components/shared/IconButton.vue'
import LoadingContainer from '@/components/widgets/LoadingContainer.vue'
import { type RiskCategory } from '@/models/api-generated'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { v4 as uuidv4 } from 'uuid'
import { viewing } from '@/services/utils'

const props = defineProps<{
	isEdit?: boolean
}>()

const isLoading = ref(false)
const model = defineModel<RiskCategory[]>({ required: true })
const deletedCategoryIds = defineModel<Set<number>>('deletedCategoryIds', { required: true })
const { t } = useI18n()

const removeNew = (index: number) => {
	model.value.splice(index, 1)
}

const markDeleteStatus = (id: number) =>
	deletedCategoryIds.value.has(id) ? deletedCategoryIds.value.delete(id) : deletedCategoryIds.value.add(id)

const newRow = () => {
	const row: RiskCategory = {
		id: 0,
		metaId: 0,
		name: '',
		instanceId: viewing.value,
		isCustom: true,
	}

	model.value.push(row)
}

const tempKeys = new WeakMap<RiskCategory, string>()

const rowKey = (c: RiskCategory) => {
	if (c.metaId) return c.metaId
	let key = tempKeys.get(c)
	if (!key) {
		key = uuidv4()
		tempKeys.set(c, key)
	}
	return key
}
</script>

<template>
	<div class="card p-3 m-3">
		<div class="row mb-2">
			<div class="col">
				<h3>{{ $t('risk.category', 2) }}</h3>
				<LoadingContainer v-model="isLoading" class="mt-3 p-3">
					<div class="table-responsive w-100">
						<table class="table">
							<thead>
								<tr>
									<th>{{ t('risk.name') }}</th>
									<th colspan="2">{{ t('risk.custom') }}</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(c, i) in model" :key="rowKey(c)" :class="{ 'table-danger': deletedCategoryIds.has(c.metaId) }">
									<td>
										<input
											v-if="(c.isCustom && props.isEdit) || !c.metaId"
											v-model="c.name"
											:data-test="'category-name-input-' + i"
											type="text"
											class="form-control"
										/>
										<span v-else>{{ c.name }}</span>
									</td>
									<td>{{ c.isCustom ? t('misc.yes') : t('misc.no') }}</td>
									<td class="text-end">
										<IconButton
											v-if="c.isCustom && props.isEdit"
											:data-test="'category-delete-toggle' + i"
											class="bi bi-trash text-danger"
											@click="c.metaId ? markDeleteStatus(c.metaId) : removeNew(i)"
											:title="$t('misc.delete')"
											size="fs-5"
										/>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</LoadingContainer>
				<div class="d-flex justify-content-center" v-if="props.isEdit">
					<IconButton data-test="add-new-category" :title="t('misc.add')" class="bi bi-plus-circle" @click="newRow" />
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="sass" scoped></style>
